<aura-dialog-layout [title]="dialogTitle()" [hideCancel]="true">
  <div dialog-content class="flex flex-col gap-5 py-2 max-h-[75vh] overflow-y-auto pr-1">
    <!-- Visuel principal & informations clés -->
    <div
      class="relative w-full rounded-xl overflow-hidden border border-neutral-4 dark:border-neutral-7 bg-neutral-2 dark:bg-neutral-1"
    >
      @if (coverPhotoUrl()) {
        <img
          [src]="coverPhotoUrl()"
          [alt]="property.presentation?.title || 'Photo du bien'"
          class="w-full h-44 sm:h-52 object-cover"
        />
      } @else {
        <div
          class="w-full h-36 sm:h-44 flex flex-col items-center justify-center bg-gradient-to-br from-primary-1/50 to-primary-3/30 dark:from-primary-11/20 dark:to-neutral-1 text-primary dark:text-primary-contrast gap-2"
        >
          <mat-icon class="!w-12 !h-12 text-4xl">home</mat-icon>
          <span class="text-xs font-medium text-neutral-10">Aucune photo principale</span>
        </div>
      }

      <!-- Badges flottants sur le visuel -->
      <div class="absolute bottom-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap items-center gap-1.5">
          <span
            class="px-2.5 py-1 rounded-md text-xs font-semibold bg-neutral-12/85 text-white backdrop-blur-md shadow-xs"
          >
            {{ propertyTypeLabel() }}
          </span>
          @if (surface()) {
            <span
              class="px-2.5 py-1 rounded-md text-xs font-medium bg-neutral-12/85 text-white backdrop-blur-md shadow-xs"
            >
              {{ surface() }} m²
            </span>
          }
          @if (roomCount()) {
            <span
              class="px-2.5 py-1 rounded-md text-xs font-medium bg-neutral-12/85 text-white backdrop-blur-md shadow-xs"
            >
              {{ roomCount() }} pièces
            </span>
          }
        </div>

        @if (price()) {
          <span class="px-3 py-1 rounded-md text-sm font-bold bg-primary text-white shadow-sm">
            {{ price() | number: '1.0-0' }} €
          </span>
        }
      </div>
    </div>

    <!-- En-tête : Titre & Localisation -->
    <div class="flex flex-col gap-1">
      <h2 class="text-lg sm:text-xl font-bold tracking-tight text-neutral-12 m-0">
        {{ property.presentation?.title || propertyTypeLabel() }}
      </h2>
      @if (property.location?.address?.city; as city) {
        <div class="flex items-center gap-1.5 text-sm text-neutral-10">
          <mat-icon class="!w-4 !h-4 text-base text-neutral-9">place</mat-icon>
          <span>
            @if (property.location?.address?.street) {
              {{ property.location.address.street }},
            }
            {{ city }}
            @if (property.location?.address?.zipCode) {
              ({{ property.location.address.zipCode }})
            }
          </span>
        </div>
      }
    </div>

    <!-- Carte de l'invitant -->
    <div
      class="p-3.5 rounded-xl border border-primary-4 dark:border-primary-8 bg-primary-1/30 dark:bg-primary-11/15 flex items-center justify-between gap-3"
    >
      <div class="flex items-center gap-3 min-w-0">
        <aura-avatar [label]="inviterName()" [srcImage]="inviterAvatar()" [size]="'md'" />
        <div class="flex flex-col min-w-0">
          <span class="text-xs font-semibold uppercase tracking-wider text-primary">
            Invitation envoyée par
          </span>
          <span class="text-sm font-bold text-neutral-12 truncate">
            {{ inviterName() }}
          </span>
        </div>
      </div>

      <span
        class="px-2.5 py-1 rounded-md text-xs font-medium shrink-0"
        [class]="
          isViewer()
            ? 'bg-neutral-3 dark:bg-neutral-2 text-neutral-11 dark:text-neutral-9'
            : 'bg-primary-2 dark:bg-primary-9 text-primary dark:text-white'
        "
      >
        {{ roleLabel() }}
      </span>
    </div>

    <!-- Grille des caractéristiques clés -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
      <div
        class="p-3 rounded-lg border border-neutral-4 dark:border-neutral-7 bg-neutral-2/50 dark:bg-neutral-1 flex flex-col gap-0.5"
      >
        <span class="text-xs text-neutral-10">Type de bien</span>
        <span class="text-sm font-semibold text-neutral-12">{{ propertyTypeLabel() }}</span>
      </div>

      <div
        class="p-3 rounded-lg border border-neutral-4 dark:border-neutral-7 bg-neutral-2/50 dark:bg-neutral-1 flex flex-col gap-0.5"
      >
        <span class="text-xs text-neutral-10">Surface habitable</span>
        <span class="text-sm font-semibold text-neutral-12">
          {{ surface() ? surface() + ' m²' : 'Non renseignée' }}
        </span>
      </div>

      <div
        class="p-3 rounded-lg border border-neutral-4 dark:border-neutral-7 bg-neutral-2/50 dark:bg-neutral-1 flex flex-col gap-0.5"
      >
        <span class="text-xs text-neutral-10">Pièces / Chambres</span>
        <span class="text-sm font-semibold text-neutral-12">
          {{ roomCount() ? roomCount() + ' pièces' : '-' }}
        </span>
      </div>

      <div
        class="p-3 rounded-lg border border-neutral-4 dark:border-neutral-7 bg-neutral-2/50 dark:bg-neutral-1 flex flex-col gap-0.5"
      >
        <span class="text-xs text-neutral-10">Classe énergétique</span>
        @if (property.dpe?.energyClass; as energy) {
          <div class="flex items-center gap-1.5 mt-0.5">
            <span
              class="inline-flex items-center justify-center font-bold text-xs h-5 w-5 rounded text-white shadow-2xs"
              [style.background-color]="dpeColor()"
              [class.text-black!]="['C', 'D', 'E'].includes(energy.toUpperCase())"
            >
              {{ energy.toUpperCase() }}
            </span>
            @if (property.dpe?.climateClass; as ges) {
              <span class="text-xs text-neutral-10">GES {{ ges.toUpperCase() }}</span>
            }
          </div>
        } @else {
          <span class="text-sm font-medium text-neutral-9 italic">Vierge</span>
        }
      </div>
    </div>

    <!-- Description du bien si renseignée -->
    @if (property.presentation?.description; as desc) {
      <div
        class="flex flex-col gap-1.5 p-3.5 rounded-lg border border-neutral-4 dark:border-neutral-7 bg-neutral-2/30 dark:bg-neutral-1"
      >
        <span class="text-xs font-semibold text-neutral-11 uppercase tracking-wider">Description</span>
        <p class="text-xs text-neutral-11 leading-relaxed m-0 line-clamp-4">
          {{ desc }}
        </p>
      </div>
    }

    <!-- Rôle et droits associés -->
    <div
      class="p-4 rounded-xl border border-neutral-4 dark:border-neutral-7 bg-neutral-2/40 dark:bg-neutral-1 flex flex-col gap-2"
    >
      <div class="flex items-center gap-2 text-xs font-semibold text-neutral-12 uppercase tracking-wider">
        <mat-icon class="!w-4 !h-4 text-base text-primary">verified_user</mat-icon>
        <span>Vos droits en tant que {{ isViewer() ? 'spectateur' : 'co-vendeur' }}</span>
      </div>
      @if (isViewer()) {
        <ul class="text-xs text-neutral-11 flex flex-col gap-1.5 m-0 pl-4 list-disc leading-relaxed">
          <li>Accès complet en consultation à la fiche du bien, aux diagnostics et aux documents déposés.</li>
          <li>
            Consultation en temps réel des candidatures et estimations proposées par les agents immobiliers.
          </li>
          <li>Accès en lecture seule, sans possibilité de modifier la fiche ou d'engager un mandat.</li>
        </ul>
      } @else {
        <ul class="text-xs text-neutral-11 flex flex-col gap-1.5 m-0 pl-4 list-disc leading-relaxed">
          <li>Accès complet à la fiche du bien, aux diagnostics et aux documents déposés.</li>
          <li>
            Consultation en temps réel des candidatures et estimations proposées par les agents immobiliers.
          </li>
          <li>Participation aux échanges de messagerie et au choix du mandat de vente.</li>
        </ul>
      }
    </div>
  </div>

  <!-- Actions en bas de modale -->
  <div
    dialog-actions
    class="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 w-full pt-3 border-t border-neutral-4 dark:border-neutral-7"
  >
    <button mat-stroked-button type="button" (click)="onClose()">
      <span>Fermer</span>
    </button>

    <button mat-stroked-button type="button" class="primary destructive" (click)="onDecline()">
      <mat-icon class="!w-4 !h-4 text-base mr-1">close</mat-icon>
      <span>Refuser l'invitation</span>
    </button>

    <button
      mat-flat-button
      class="primary flex items-center justify-center gap-1.5"
      type="button"
      (click)="onAccept()"
    >
      <mat-icon class="!w-4 !h-4 text-base">check</mat-icon>
      <span>{{ isViewer() ? "Accepter l'accès" : 'Accepter la co-gestion' }}</span>
    </button>
  </div>
</aura-dialog-layout>
